import React, { useEffect, useState } from 'react';
import ModalCard from '../ModalCard';
import UsersInfo from './components/UsersInfo';
import ModalPrivileges from '../ModalPrivileges';
import { useApi } from '../../../hooks';
import { getUsersRequest } from '../../../api/userApi';
import {
  addPrivilegeRoleRequest,
  createRoleRequest,
  deleteRoleRequest, getRolePrivilegesRequest, removeRolePrivilegeRequest,
  updateRoleRequest,
} from '../../../api/rolesApi';
import { compareId, formValidator } from '../../../helpers';
import { useModalStore } from '../../../store';
import { getPrivilegesRequest } from '../../../api/privilegeApi';
import PrivilegeCheckboxes from '../../PrivilegeCheckboxes';
import { HAS_REQUIRED_FIELDS_TEXT } from '../../../helpers/formValidator';

const roleFrame = [
  {
    id: 1,
    title: 'Наименование',
    inputFields: [
      { id: 'name', label: 'Наименование' },
      { id: 'code', label: 'Код' },
    ],
  },
];

const initialRoleData = {
  name: '',
  code: '',
  privilege: {},
};

export default function ModalRole({
  closeModal, onClick, roleData, updateRoles, isCreateRoleModal,
}) {
  const { modalStore, setModalStore } = useModalStore();
  const [role, setRole] = useState(initialRoleData);
  const [initialPrivileges, setInitialPrivileges] = useState(null);
  const [currentRoleUsers, setCurrentRoleUsers] = useState([]);
  const [error, setError] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [privileges, setPrivileges] = useState(null);
  const [isPrivilegesModalOpen, setIsPrivilegesModalOpen] = useState(false);

  useApi({
    setter: (rolePrivileges) => {
      console.log('rolePrivileges:', rolePrivileges);
      const newPrivilegeData = {};
      privileges.forEach((privilege) => {
        // Находим все PrivilegeRole для этой привилегии (может быть несколько для разных районов)
        const matchingPrivilegeRoles = rolePrivileges
          ?.filter((
            rolePrivilege,
          ) => rolePrivilege.privilege_attributes?.attributes?.code === privilege.code) || [];

        if (privilege.code === 'tsoo_active_objects_create') {
          console.log('matchingPrivilegeRoles for tsoo_active_objects_create:', matchingPrivilegeRoles);
        }

        const hasPrivilege = matchingPrivilegeRoles.length > 0;
        const firstMatch = matchingPrivilegeRoles[0];

        // Собираем все municipal_district_id
        const municipalDistrictIds = matchingPrivilegeRoles
          .map(rp => rp.municipal_district_id)
          .filter(id => id != null);

        // Маппинг district_id -> privilege_role_id для удаления
        const districtToPrivilegeRoleId = {};
        matchingPrivilegeRoles.forEach(rp => {
          if (rp.municipal_district_id != null) {
            districtToPrivilegeRoleId[rp.municipal_district_id] = rp.id;
          }
        });

        // Собираем все privilege_role_ids для удаления при снятии привилегии
        const allPrivilegeRoleIds = matchingPrivilegeRoles.map(rp => rp.id);

        newPrivilegeData[privilege.code] = {
          value: hasPrivilege,
          ...firstMatch,
          ...privilege,
          privilege_roles_id: firstMatch?.id,
          privilege_roles_ids: allPrivilegeRoleIds,
          municipal_district_ids: municipalDistrictIds,
          district_to_privilege_role_id: districtToPrivilegeRoleId,
          privilege_group_attributes: privilege.privilege_group_attributes,
        };
      });
      setRole((prevState) => {
        setInitialPrivileges({ ...newPrivilegeData });

        return {
          ...prevState,
          privilege: { ...newPrivilegeData },
        };
      });
    },
    setIsLoading,
    shouldRequest: privileges?.length,
    request: () => getRolePrivilegesRequest(role.id, { total: 10000 }),
  });

  useApi({
    setter: setPrivileges,
    setIsLoading,
    shouldRequest: true,
    request: () => getPrivilegesRequest({ total: 10000 }),
  });

  const handleChange = (field) => (event, value) => {
    setRole((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  const getCurrentRoleUser = async () => {
    const newUsers = await getUsersRequest({ role_id: role.id });

    setCurrentRoleUsers(newUsers.filter(({ role_id }) => compareId(role_id, role.id)));
  };

  const { makeRequest: onGetRoleUser } = useApi({
    request: getCurrentRoleUser,
  });

  const changePrivilegeCheckbox = (e, changedPrivilegeCheckbox) => {
    const newPrivilege = {
      ...role.privilege,
      [e.target.name]: {
        ...role.privilege[e.target.name],
        value: changedPrivilegeCheckbox,
      },
    };

    handleChange('privilege')(e, newPrivilege);
  };

  const createUpdateRole = async (requestType) => {
    const { name, code, id } = role;

    const newRole = { name, code };

    const { hasErrors, validField } = formValidator({
      form: { name },
    });

    setError(validField);

    if (hasErrors) {
      throw { frontendError: HAS_REQUIRED_FIELDS_TEXT };
    }

    const addedPrivileges = [];
    const removedPrivileges = [];

    Object.values(role.privilege).forEach((privilege) => {
      const initialValues = requestType === 'update' ? Object.values(initialPrivileges) : privileges;

      const changedPrivilege = initialValues
        .find((initialPrivilege) => initialPrivilege.id === privilege.id);

      if (changedPrivilege.value !== privilege.value) {
        const privilegeId = privilege.id;

        if (privilege.value) {
          // Если есть выбранные муниципальные районы, создаем отдельную запись для каждого
          const districtIds = privilege.municipal_district_ids || [];
          if (districtIds.length > 0) {
            districtIds.forEach((districtId) => {
              addedPrivileges.push({ privilege_id: privilegeId, municipal_district_id: districtId });
            });
          } else {
            addedPrivileges.push({ privilege_id: privilegeId });
          }
        } else {
          // Удаляем все privilege_roles для этой привилегии
          const allIds = privilege.privilege_roles_ids || [privilege.privilege_roles_id];
          allIds.forEach(id => {
            if (id) removedPrivileges.push(id);
          });
        }
      } else if (privilege.value) {
        // Проверяем изменились ли муниципальные районы
        const oldDistrictIds = changedPrivilege.municipal_district_ids || [];
        const newDistrictIds = privilege.municipal_district_ids || [];

        const oldSet = new Set(oldDistrictIds);
        const newSet = new Set(newDistrictIds);

        // Добавленные районы
        newDistrictIds.forEach((districtId) => {
          if (!oldSet.has(districtId)) {
            addedPrivileges.push({ privilege_id: privilege.id, municipal_district_id: districtId });
          }
        });

        // Удаленные районы
        const districtMapping = changedPrivilege.district_to_privilege_role_id || {};
        oldDistrictIds.forEach((districtId) => {
          if (!newSet.has(districtId)) {
            const privilegeRoleId = districtMapping[districtId];
            if (privilegeRoleId) {
              removedPrivileges.push(privilegeRoleId);
            }
          }
        });
      }
    });

    let role_id = id;

    if (addedPrivileges.length) {
      await addPrivilegeRoleRequest(
        addedPrivileges.map((p) => ({ ...p, role_id })),
      );
    }
    if (removedPrivileges.length) await removeRolePrivilegeRequest(removedPrivileges);

    await updateRoles();

    if (requestType === 'update') {
      await updateRoleRequest(id, newRole);
    } else if (requestType === 'create') {
      const createdRole = await createRoleRequest(newRole);
      role_id = createdRole?.id;
    }

    closeModal();
  };

  const { makeRequest: onCreateUpdateRole } = useApi({
    request: createUpdateRole,
    successMessage: `Роль ${isCreateRoleModal ? 'создана' : 'обновлена'}`,
  });

  const onDeleteRole = () => {
    setModalStore({
      ...modalStore,
      deleteModal: {
        isOpen: true,
        callback: async () => {
          await updateRoles();
          closeModal();
        },
        deleteRequest: deleteRoleRequest,
        id: role.id,
        objectName: 'роль',
        message: 'Роль удалена',
      },
    });
  };

  useEffect(() => {
    if (roleData?.id) {
      setRole((prevState) => ({
        ...prevState,
        ...roleData,
      }));
    }
  }, [roleData]);

  useEffect(() => {
    if (role.id) onGetRoleUser();
    /* eslint-disable-next-line */
  }, [role?.id]);

  // Разделяем привилегии на TSOO и остальные
  const tsooPrivileges = Object.fromEntries(
    Object.entries(role.privilege).filter(([code]) => code.startsWith('tsoo_'))
  );
  const otherPrivileges = Object.fromEntries(
    Object.entries(role.privilege).filter(([code]) => !code.startsWith('tsoo_'))
  );

  const hasTsooPrivileges = Object.keys(tsooPrivileges).length > 0;

  return (
    <>
      <ModalCard
        inputs={roleFrame}
        open
        close={closeModal}
        isLoading={isLoading}
        handleChange={handleChange}
        containerMaxWidth="lg"
        inputData={role}
        error={error}
        firstButtonProp={!isCreateRoleModal && {
          label: 'Удалить роль',
          onClick: onDeleteRole,
        }}
        secondButtonProp={{
          label: 'Сохранить',
          onClick: () => onCreateUpdateRole(isCreateRoleModal ? 'create' : 'update'),
        }}
        additionalComponents={(
          <UsersInfo users={currentRoleUsers} openModal={onClick} />
        )}
        additionalContainer={
          Object.keys(otherPrivileges).length > 0 && (
            <PrivilegeCheckboxes
              privilegeData={otherPrivileges}
              setRole={setRole}
              onChange={changePrivilegeCheckbox}
              onManageTsooRights={hasTsooPrivileges ? () => setIsPrivilegesModalOpen(true) : null}
            />
          )
        }
      />

      {isPrivilegesModalOpen && hasTsooPrivileges && (
        <ModalPrivileges
          open={isPrivilegesModalOpen}
          close={() => setIsPrivilegesModalOpen(false)}
          privilegeData={tsooPrivileges}
          setRole={setRole}
          onChange={changePrivilegeCheckbox}
          onSave={() => setIsPrivilegesModalOpen(false)}
        />
      )}
    </>
  );
}
